import { TUseSearchParams } from '@/hooks/use-search-params.ts';
import { Checkbox } from '@/components/ui/checkbox.tsx';
import { Slider } from '@/components/ui/slider.tsx';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '@/stores/auth.store.ts';

type Props = {
  useSearch: TUseSearchParams;
};

function getStep(value: number | number[]): number {
  if (Array.isArray(value)) {
    return value[0];
  }
  return value;
}

export function FacetSize({useSearch}: Props) {
  const { user } = useAuth();
  const isUk = useMemo(() => user.site_id === 2, [user.site_id]);
  const [hasWidth, setHasWidth] = useState(useSearch?.has('wcm') ?? false);
  const [widthSteps, setWidthSteps] = useState(useSearch?.has('wcm') ? Math.round((Number(useSearch.get('wcm')) / 2.54)) : 96);
  const [widthStepsCommit, setWidthStepsCommit] = useState(widthSteps);
  const [widthMarginSteps, setWidthMarginSteps] = useState(useSearch?.has('wcm_margin') ? Math.round((Number(useSearch.get('wcm_margin')) / 2.54)) : 6);
  const [widthMarginStepsCommit, setWidthMarginStepsCommit] = useState(widthMarginSteps);
  const widthLabel = useMemo(() => {
    if (!hasWidth) {
      return ''
    }
    const totalInches = widthSteps;
    if (isUk) {
      const meters = (totalInches / 39.37).toFixed(2);
      return `${meters} m`;
    } else {
      const feet = Math.floor(totalInches / 12);
      const inches = totalInches % 12;
      return `${feet}' ${inches}"`;
    }
  }, [widthSteps, isUk, hasWidth]);
  const widthMarginLabel = useMemo(() => {
    if (!hasWidth) {
      return ''
    }
    const totalInches = widthMarginSteps;
    if (isUk) {
      const cm = (totalInches * 2.54).toFixed(1);
      return `${cm} cm`;
    } else {
      const feet = Math.floor(totalInches / 12);
      const inches = totalInches % 12;
      return `${feet}' ${inches}"`;
    }
  }, [widthMarginSteps, isUk, hasWidth]);

  const [hasLength, setHasLength] = useState(useSearch?.has('lcm') ?? false);
  const [lengthSteps, setLengthSteps] = useState(useSearch?.has('lcm') ? Math.round((Number(useSearch.get('lcm')) / 2.54)) : 120);
  const [lengthStepsCommit, setLengthStepsCommit] = useState(lengthSteps);
  const [lengthMarginSteps, setLengthMarginSteps] = useState(useSearch?.has('lcm_margin') ? Math.round((Number(useSearch.get('lcm_margin')) / 2.54)) : 6);
  const [lengthMarginStepsCommit, setLengthMarginStepsCommit] = useState(lengthMarginSteps);
  const lengthLabel = useMemo(() => {
    if (!hasLength) {
      return ''
    }
    const totalInches = lengthSteps;
    if (isUk) {
      const meters = (totalInches / 39.37).toFixed(2);
      return `${meters} m`;
    } else {
      const feet = Math.floor(totalInches / 12);
      const inches = totalInches % 12;
      return `${feet}' ${inches}"`;
    }
  }, [lengthSteps, isUk, hasLength]);
  const lengthMarginLabel = useMemo(() => {
    if (!hasLength) {
      return ''
    }
    const totalInches = lengthMarginSteps;
    if (isUk) {
      const cm = (totalInches * 2.54).toFixed(1);
      return `${cm} cm`;
    } else {
      const feet = Math.floor(totalInches / 12);
      const inches = totalInches % 12;
      return `${feet}' ${inches}"`;
    }
  }, [lengthMarginSteps, isUk, hasLength]);
  const updated = useRef(false);

  useEffect(() => {
    if (!updated.current) {
      return;
    }
    const widthCms = hasWidth ? widthStepsCommit * 2.54 : undefined;
    const widthMarginCms = hasWidth ? widthMarginStepsCommit * 2.54 : undefined;
    const lengthCms = hasLength ? lengthStepsCommit * 2.54 : undefined;
    const lengthMarginCms = hasLength ? lengthMarginStepsCommit * 2.54 : undefined;
    useSearch?.setParams({
      wcm: widthCms,
      wcm_margin: widthMarginCms,
      lcm: lengthCms,
      lcm_margin: lengthMarginCms,
    });
  }, [widthStepsCommit, widthMarginStepsCommit, lengthStepsCommit, lengthMarginStepsCommit, hasWidth, hasLength]);

  useEffect(() => {
    if (hasWidth && !useSearch.has('wcm')) {
      setHasWidth(false);
    }
    if (hasLength && !useSearch.has('lcm')) {
      setHasLength(false);
    }
  }, [useSearch?.params]);
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center gap-3">
            <Checkbox checked={hasWidth} onCheckedChange={() => {
              updated.current = true;
              setHasWidth(!hasWidth);
            }} />
            <span>Width</span>
          </div>
          <span>{widthLabel}</span>
        </div>
        <div>
          <Slider
            defaultValue={[widthSteps]}
            max={360}
            step={1}
            onValueChange={(v) => setWidthSteps(getStep(v))}
            onValueCommit={(v) => {
              updated.current = true;
              setWidthStepsCommit(getStep(v));
            }}
            disabled={!hasWidth}
          />
        </div>
      </div>
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center gap-3">
            <span>Width Margin</span>
          </div>
          <span>{widthMarginLabel}</span>
        </div>
        <div>
          <Slider
            defaultValue={[widthMarginSteps]}
            max={36}
            step={1}
            onValueChange={(v) => setWidthMarginSteps(getStep(v))}
            onValueCommit={(v) => {
              updated.current = true;
              setWidthMarginStepsCommit(getStep(v));
            }}
            disabled={!hasWidth}
          />
        </div>
      </div>
      <div className="flex flex-col gap-3 mt-4">
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center gap-3">
            <Checkbox checked={hasLength} onCheckedChange={() => {
              updated.current = true;
              setHasLength(!hasLength);
            }} />
            <span>Length</span>
          </div>
          <span>{lengthLabel}</span>
        </div>
        <div>
          <Slider
            defaultValue={[lengthSteps]}
            max={360}
            step={1}
            onValueChange={(v) => setLengthSteps(getStep(v))}
            onValueCommit={(v) => {
              updated.current = true;
              setLengthStepsCommit(getStep(v));
            }}
            disabled={!hasLength}
          />
        </div>
      </div>
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center gap-3">
            <span>Length Margin</span>
          </div>
          <span>{lengthMarginLabel}</span>
        </div>
        <div>
          <Slider
            defaultValue={[lengthMarginSteps]}
            max={36}
            step={1}
            onValueChange={(v) => setLengthMarginSteps(getStep(v))}
            onValueCommit={(v) => {
              updated.current = true;
              setLengthMarginStepsCommit(getStep(v));
            }}
            disabled={!hasLength}
          />
        </div>
      </div>
    </div>
  );
}
